import { CombinedError } from 'urql';

const DEFAULT_ERROR_MESSAGE = 'An unknown error has occurred. We are looking into it.';

/**
 * The message to show for a caught error. A urql `CombinedError` is unwrapped to the server's own
 * message, since its `message` carries a "[GraphQL]" or "[Network]" prefix meant for developers.
 */
export function handleError(e: unknown, fallback: string = DEFAULT_ERROR_MESSAGE): string {
  if (e instanceof CombinedError) {
    return getCombinedErrorMessage(e, fallback);
  }

  if (e instanceof Error) {
    return e.message;
  }

  return fallback;
}

export function getCombinedErrorMessage(error: CombinedError, fallback: string): string {
  // Coerce to a real string: a non-string message (e.g. an object) rendered in a
  // toast/JSX throws React error #31 and blanks the screen.
  const message = error.graphQLErrors[0]?.message ?? error.networkError?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/**
 * urql resolves a failed mutation with `error` set and does not throw, so a `catch` never sees it.
 * Pass the result through this, then show the caught error with `handleError`.
 */
export function throwIfMutationError<Result extends { error?: CombinedError }>(result: Result): Result {
  if (result.error) {
    throw result.error;
  }
  return result;
}

const prefix = 'Invariant failed';

export function invariant(condition: any, message?: string | (() => string)): asserts condition {
  if (condition) {
    return;
  }
  // Condition not passed

  const provided: string | undefined = typeof message === 'function' ? message() : message;

  // Options:
  // 1. message provided: `${prefix}: ${provided}`
  // 2. message not provided: prefix
  const value: string = provided ? `${prefix}: ${provided};` : prefix;
  throw new Error(value);
}
